import React, { useState, useEffect, useRef } from 'react';
import BASE_URL, { SEO_BASE_URL } from '../config';
import {
  FaMapMarkedAlt,
  FaPlus,
  FaEye,
  FaRedo,
  FaTrash,
  FaMapMarkerAlt,
  FaHeart,
  FaCalendarAlt,
  FaGlobeAmericas,
  FaUsers,
  FaStar,
  FaRegStar,
  FaSpinner
} from 'react-icons/fa';
import { getUserItineraries, deleteItinerary, getMemberItineraries, getPhotos, getItinerariesStatus } from '../api/client';
import GroupMembers from './GroupMembers';
import { MiniStarRating } from './RatingFeedback';
import SEO from './SEO';
import { SkeletonCardGrid } from './SkeletonCard';
import MaintenanceBanner from './MaintenanceBanner';

// Members keep full access for 14 days after end_date — MUST match backend
// SubscriptionService.MEMBER_ACCESS_GRACE_DAYS and ItineraryDisplay.
const MEMBER_ACCESS_GRACE_DAYS = 14;

// Helper: has the trip's member-access window closed (14 days past end_date)?
// Parses date-only strings in LOCAL time to avoid a UTC off-by-one for US users.
const isTripEnded = (endDate) => {
  if (!endDate) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(endDate));
  const tripEnd = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(endDate);
  if (isNaN(tripEnd)) return false;
  tripEnd.setHours(0, 0, 0, 0);
  tripEnd.setDate(tripEnd.getDate() + MEMBER_ACCESS_GRACE_DAYS);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today > tripEnd;
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'cancelling']);

const isSubscriptionActive = (user) => {
  if (!user) return false;
  if (user.pay_as_you_go && (user.trip_credits || 0) > 0) return true;
  if (!ACTIVE_SUBSCRIPTION_STATUSES.has(user.subscription_status)) return false;
  if (user.subscription_end) {
    const end = new Date(user.subscription_end);
    if (!Number.isNaN(end.getTime()) && end <= new Date()) return false;
  }
  return true;
};

// True only if the user's own tier permits creating new itineraries.
// Free-tier members of a Group plan have an "active" status but cannot create —
// route them to /pricing instead of letting them hit a 403 from the API.
const TIERS_WITHOUT_CREATE = new Set(['free', 'demo']);
const canCreateItineraries = (user) => {
  if (!isSubscriptionActive(user)) return false;
  if ((user.trip_credits || 0) > 0) return true;
  return !TIERS_WITHOUT_CREATE.has(user.subscription_tier);
};

const Dashboard = ({ user, onLogout, onNewItinerary, onViewItinerary, onRegenerateItinerary, onViewProfile, loadingItineraryId, onViewConfiguredItineraries, onViewPricing }) => {
  const subscriptionActive = isSubscriptionActive(user);
  const canCreate = canCreateItineraries(user);
  const handlePlanNewTrip = canCreate ? onNewItinerary : onViewPricing;
  const [itineraries, setItineraries] = useState([]);
  const [memberItineraries, setMemberItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [membersItineraryId, setMembersItineraryId] = useState(null);
  const [cardImageUrls, setCardImageUrls] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  // Refs to track loading state and blob URLs
  const blobUrlsRef = useRef({});
  const loadingRef = useRef({}); // Track which itineraries are currently loading
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadAllData();
    return () => {
      mountedRef.current = false;
      // Revoke all blob URLs on unmount
      Object.values(blobUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Load cover images or fallback to random photo thumbnails for itineraries
  useEffect(() => {
    const runtimeConfig = window.__RUNTIME_CONFIG__ || {};
    const API_BASE_URL = runtimeConfig.API_URL || process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

    const fetchCoverImage = async (requestId) => {
      const token = localStorage.getItem('access_token');
      const cacheBust = Date.now();
      const response = await fetch(`${API_BASE_URL}/itinerary/${requestId}/cover?_t=${cacheBust}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      if (!response.ok) {
        return null; // Cover image not available
      }
      return await response.blob();
    };

    const fetchPhotoBlob = async (photoId) => {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/photos/${photoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch photo');
      }
      return await response.blob();
    };

    const loadPhotoForItinerary = async (requestId) => {
      // Skip if already loaded or currently loading
      if (blobUrlsRef.current[requestId] || loadingRef.current[requestId]) {
        return;
      }

      // Mark as loading
      loadingRef.current[requestId] = true;

      try {
        // First, try to fetch the cover image (generated from destination)
        const coverBlob = await fetchCoverImage(requestId);
        if (!mountedRef.current) return;

        if (coverBlob) {
          const url = URL.createObjectURL(coverBlob);
          blobUrlsRef.current[requestId] = url;
          setCardImageUrls(prev => ({ ...prev, [requestId]: url }));
          return; // Cover image loaded successfully
        }

        // Fallback: Try to load a random photo from the gallery
        const data = await getPhotos(requestId);
        if (!mountedRef.current) return;

        const photos = data.photos || [];
        if (photos.length > 0) {
          const randomIndex = Math.floor(Math.random() * photos.length);
          const randomPhoto = photos[randomIndex];

          const blob = await fetchPhotoBlob(randomPhoto.id);
          if (!mountedRef.current) return;

          const url = URL.createObjectURL(blob);
          blobUrlsRef.current[requestId] = url;
          setCardImageUrls(prev => ({ ...prev, [requestId]: url }));
        }
      } catch (err) {
        console.error(`Failed to load image for ${requestId}:`, err);
      } finally {
        // Clear loading flag (allows retry on error)
        loadingRef.current[requestId] = false;
      }
    };

    const allItineraries = [...itineraries, ...memberItineraries];

    // Load photos with concurrency limit to avoid overloading API
    const loadPhotosWithLimit = async () => {
      const CONCURRENT_LIMIT = 3;
      for (let i = 0; i < allItineraries.length; i += CONCURRENT_LIMIT) {
        if (!mountedRef.current) return;
        const batch = allItineraries.slice(i, i + CONCURRENT_LIMIT);
        await Promise.all(batch.map(itin => loadPhotoForItinerary(itin.request_id)));
      }
    };

    loadPhotosWithLimit();

    // No cleanup needed - individual loads manage themselves via refs
  }, [itineraries, memberItineraries]);

  const loadAllData = async () => {
    setLoading(true);
    setError('');
    try {
      // Run both API calls in parallel for faster loading
      const [userItins, memberItins] = await Promise.all([
        getUserItineraries().catch(err => {
          console.error('Error loading itineraries:', err);
          if (err.response?.status !== 404) {
            setError('Failed to load itineraries. Please try again.');
          }
          return [];
        }),
        getMemberItineraries().catch(err => {
          console.error('Error loading member itineraries:', err);
          return { itineraries: [] };
        })
      ]);

      setItineraries(userItins || []);
      setMemberItineraries(memberItins?.itineraries || []);
    } finally {
      setLoading(false);
    }
  };

  const loadItineraries = async () => {
    try {
      const data = await getUserItineraries();
      setItineraries(data || []);
    } catch (err) {
      console.error('Error loading itineraries:', err);
      setItineraries([]);
    }
  };

  // Poll for updates when there are processing itineraries (batch status check)
  useEffect(() => {
    const processingItineraries = itineraries.filter(itin => itin.status === 'processing');
    const processingMemberItineraries = memberItineraries.filter(itin => itin.status === 'processing');

    if (processingItineraries.length > 0 || processingMemberItineraries.length > 0) {
      const pollInterval = setInterval(async () => {
        // Collect all processing request IDs
        const allProcessingIds = [
          ...processingItineraries.map(i => i.request_id),
          ...processingMemberItineraries.map(i => i.request_id)
        ];

        if (allProcessingIds.length === 0) return;

        try {
          // Single batch request for all statuses
          const data = await getItinerariesStatus(allProcessingIds);
          const statuses = data.statuses || {};

          // Update user's itineraries that changed status
          setItineraries(prev => prev.map(itin => {
            const newStatus = statuses[itin.request_id];
            if (newStatus && newStatus !== 'processing' && itin.status === 'processing') {
              return { ...itin, status: newStatus };
            }
            return itin;
          }));

          // Update member's itineraries that changed status
          setMemberItineraries(prev => prev.map(itin => {
            const newStatus = statuses[itin.request_id];
            if (newStatus && newStatus !== 'processing' && itin.status === 'processing') {
              return { ...itin, status: newStatus };
            }
            return itin;
          }));
        } catch (err) {
          console.error('Error polling itinerary statuses:', err);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(pollInterval);
    }
  }, [itineraries, memberItineraries]);

  const getStatusBadge = (status) => {
    const badges = {
      completed: { bg: 'bg-gradient-to-r from-adventure-400 to-adventure-500', text: 'text-white', label: '✓ Completed', icon: '🎉' },
      processing: { bg: 'bg-gradient-to-r from-primary-400 to-accent-500', text: 'text-white', label: 'Processing...', pulse: true, icon: '⏳' },
      pending: { bg: 'bg-gradient-to-r from-golden-400 to-secondary-500', text: 'text-white', label: 'Pending', icon: '📋' },
      failed: { bg: 'bg-gradient-to-r from-coral-500 to-red-500', text: 'text-white', label: 'Failed', icon: '⚠️' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span
        className={`${badge.bg} ${badge.text} px-3 py-1.5 rounded-full text-xs font-bold shadow-md ${
          badge.pulse ? 'animate-pulse' : ''
        }`}
      >
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleMembersClick = (requestId) => {
    setMembersItineraryId(requestId);
    setMembersModalOpen(true);
  };

  const handleDeleteClick = (requestId) => {
    setDeleteConfirmId(requestId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    // Store the itinerary for potential rollback
    const deletedItinerary = itineraries.find(i => i.request_id === deleteConfirmId);
    const originalIndex = itineraries.findIndex(i => i.request_id === deleteConfirmId);
    const idToDelete = deleteConfirmId;

    // Close modal and start fade-out animation
    setDeleteConfirmId(null);
    setDeletingId(idToDelete);
    setError(''); // Clear any previous error

    // Wait for fade-out animation, then remove from list
    await new Promise(resolve => setTimeout(resolve, 300));

    // Optimistic update: remove from UI
    setItineraries(prev => prev.filter(i => i.request_id !== idToDelete));
    setDeletingId(null);

    try {
      await deleteItinerary(deletedItinerary.request_id);
      // Success - card already removed, nothing more to do
    } catch (err) {
      console.error('Error deleting itinerary:', err);
      // Rollback: restore the itinerary at its original position
      setItineraries(prev => {
        const newList = [...prev];
        newList.splice(originalIndex, 0, deletedItinerary);
        return newList;
      });
      setError('Failed to delete itinerary. Please try again.');
      // Auto-clear error after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  return (
    <>
      <SEO
        title="Dashboard"
        description="Manage your AI-generated travel itineraries. View, edit, and plan your perfect trips with ItinerarySeek's intelligent trip planner."
        canonical={`${SEO_BASE_URL}/`}
      />
      {/* Maintenance Banner */}
      <MaintenanceBanner />
      {/* Subscription inactive banner — past itineraries remain viewable, but new trips are gated. */}
      {!subscriptionActive && user && (
        <div className="mb-4 bg-gradient-to-r from-amber-50 to-red-50 border-l-4 border-amber-500 text-amber-900 p-4 rounded-lg animate-slide-down flex items-start justify-between gap-4">
          <div className="text-sm">
            <p className="font-semibold mb-1">Your subscription is not active</p>
            <p>
              You can still view your past itineraries. To plan a new trip
              {user.subscription_status === 'past_due' ? ' once your payment is updated' : ''}
              , please renew your subscription.
            </p>
          </div>
          {onViewPricing && (
            <button
              onClick={onViewPricing}
              className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            >
              Renew
            </button>
          )}
        </div>
      )}
      {/* Error Banner */}
      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg animate-slide-down">
          <p className="text-sm">{error}</p>
        </div>
      )}
      <div className="animate-fade-in">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-primary-600 via-accent-500 to-adventure-500 rounded-3xl p-8 md:p-10 mb-8 text-white shadow-2xl">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary-500/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl"></div>
        <div className="absolute top-4 right-4 text-6xl opacity-20 animate-float">✈️</div>
        <div className="absolute bottom-4 left-1/3 text-4xl opacity-20 animate-float-gentle" style={{animationDelay: '1s'}}>🌍</div>

        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FaGlobeAmericas className="text-3xl text-white" />
              </div>
              <div>
                <p className="text-white/80 text-sm font-medium">Welcome back</p>
                <h1 className="text-3xl md:text-4xl font-bold font-display">
                  {user.full_name || 'Traveler'}! 👋
                </h1>
              </div>
            </div>
            <p className="text-white/90 text-lg ml-1">Ready for your next adventure? Let's explore the world together!</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {onViewConfiguredItineraries && (
              <button
                onClick={onViewConfiguredItineraries}
                className="group bg-amber-500 text-white border border-amber-400 px-6 py-4 rounded-2xl font-bold flex items-center gap-3 hover:bg-amber-600 hover:shadow-xl hover:scale-105 transition-all duration-300 shadow-lg"
              >
                <span className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                  <FaGlobeAmericas className="text-white" />
                </span>
                <span>Explore Curated Itineraries</span>
              </button>
            )}
            <button
              onClick={handlePlanNewTrip}
              title={
                canCreate
                  ? ''
                  : subscriptionActive
                    ? 'Your plan does not include creating new trips. Upgrade to plan your own.'
                    : 'Your subscription is not active. Renew to plan new trips.'
              }
              className="group bg-white text-primary-600 px-8 py-4 rounded-2xl font-bold flex items-center gap-3 hover:shadow-2xl hover:scale-105 transition-all duration-300 shadow-lg"
            >
              <span className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center group-hover:rotate-90 transition-transform duration-300">
                <FaPlus className="text-white" />
              </span>
              <span>{canCreate ? 'Plan New Trip' : subscriptionActive ? 'Upgrade to Plan' : 'Renew to Plan'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Itineraries Section */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg">
            <FaMapMarkedAlt className="text-2xl text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-800 font-display">Your Travel Plans</h2>
            <p className="text-neutral-500 text-sm">
              {loading ? (
                <span className="flex items-center gap-2">
                  <FaSpinner className="animate-spin" />
                  Loading your adventures...
                </span>
              ) : (
                `${itineraries.length} adventure${itineraries.length !== 1 ? 's' : ''} planned`
              )}
            </p>
          </div>
        </div>

        {loading ? (
          <>
            {/* Loading progress bar */}
            <div className="mb-6 bg-neutral-100 rounded-full h-1.5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 animate-pulse rounded-full w-2/3" />
            </div>
            <SkeletonCardGrid count={6} type="owner" />
          </>
        ) : error ? (
          <div className="bg-gradient-to-r from-red-50 to-coral-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <div className="text-4xl mb-4">😔</div>
            <p className="text-red-700 mb-4 font-medium">{error}</p>
            <button
              onClick={loadItineraries}
              className="bg-gradient-to-r from-red-500 to-coral-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Try Again
            </button>
          </div>
        ) : itineraries.length === 0 ? (
          <div className="relative bg-white rounded-3xl shadow-xl p-12 text-center overflow-hidden">
            {/* Decorative background */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-accent-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-60"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-secondary-100 to-golden-100 rounded-full translate-x-1/2 translate-y-1/2 opacity-60"></div>

            <div className="relative">
              <div className="w-28 h-28 bg-gradient-to-br from-primary-100 via-accent-100 to-secondary-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-5xl">🗺️</span>
              </div>
              <h3 className="text-3xl font-bold text-neutral-800 mb-3 font-display">No Adventures Yet</h3>
              <p className="text-neutral-600 mb-8 max-w-md mx-auto text-lg">
                Your travel story starts here! Let our AI craft the perfect itinerary tailored just for you.
              </p>
              <button
                onClick={handlePlanNewTrip}
                title={
                  canCreate
                    ? ''
                    : subscriptionActive
                      ? 'Your plan does not include creating new trips. Upgrade to plan your own.'
                      : 'Your subscription is not active. Renew to plan new trips.'
                }
                className="group bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white px-10 py-4 rounded-2xl font-bold flex items-center gap-3 mx-auto hover:shadow-2xl hover:scale-105 transition-all duration-300"
              >
                <span className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:rotate-90 transition-transform duration-300">
                  <FaPlus />
                </span>
                {canCreate ? 'Start Your First Adventure' : subscriptionActive ? 'Upgrade to Get Started' : 'Renew to Get Started'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {itineraries.map((itinerary, index) => (
              <div
                key={itinerary.request_id}
                className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden hover:-translate-y-2 animate-fade-in-up border border-neutral-100 ${
                  deletingId === itinerary.request_id ? 'opacity-0 scale-95 pointer-events-none' : ''
                }`}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {/* Card Header - Random Photo or Gradient */}
                <div className="relative h-40 overflow-hidden">
                  {cardImageUrls[itinerary.request_id] ? (
                    /* Random Trip Photo */
                    <img
                      src={cardImageUrls[itinerary.request_id]}
                      alt="Trip photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    /* Default Gradient Background */
                    <div className="w-full h-full bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600">
                      {/* Decorative circles */}
                      <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    </div>
                  )}
                  {/* Overlay for status and date */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex justify-between items-end">
                      {getStatusBadge(itinerary.status)}
                      <div className="flex items-center gap-1.5 text-white/90 text-sm bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
                        <FaCalendarAlt className="text-xs" />
                        <span className="font-medium">{formatDate(itinerary.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-5 space-y-4">
                  {/* Route Display */}
                  <div className="relative">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-500 rounded-full flex items-center justify-center shadow-md">
                          <FaMapMarkerAlt className="text-white text-xs" />
                        </div>
                        <div className="w-0.5 h-6 bg-gradient-to-b from-primary-400 to-secondary-400 my-1"></div>
                        <div className="w-8 h-8 bg-gradient-to-br from-secondary-400 to-secondary-500 rounded-full flex items-center justify-center shadow-md">
                          <FaMapMarkerAlt className="text-white text-xs" />
                        </div>
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <p className="text-xs text-neutral-400 uppercase tracking-wider font-semibold">From</p>
                          <p className="font-bold text-neutral-800">{itinerary.start_location}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-400 uppercase tracking-wider font-semibold">To</p>
                          <p className="font-bold text-neutral-800">{itinerary.end_location}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Travel Dates */}
                  {itinerary.start_date && (
                    <div className="flex items-center gap-3 bg-gradient-to-r from-accent-50 to-primary-50 p-3 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-accent-400 to-accent-500 rounded-xl flex items-center justify-center shadow-md">
                        <FaCalendarAlt className="text-white text-sm" />
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 font-semibold">Travel Dates</p>
                        <p className="font-bold text-neutral-800">
                          {itinerary.start_date}{itinerary.end_date ? ` → ${itinerary.end_date}` : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Interests */}
                  {itinerary.interests && itinerary.interests.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FaHeart className="text-coral-400 text-sm" />
                        <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">Interests</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {itinerary.interests.slice(0, 3).map((interest, idx) => (
                          <span
                            key={idx}
                            className="bg-gradient-to-r from-primary-100 to-accent-100 text-primary-700 px-3 py-1 rounded-full text-xs font-semibold border border-primary-200"
                          >
                            {interest}
                          </span>
                        ))}
                        {itinerary.interests.length > 3 && (
                          <span className="bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full text-xs font-semibold">
                            +{itinerary.interests.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rating Display */}
                  {itinerary.status === 'completed' && (
                    <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                      {itinerary.average_rating ? (
                        <div className="flex items-center gap-2">
                          <MiniStarRating rating={itinerary.average_rating} />
                          <span className="text-sm text-neutral-600 font-medium">
                            {itinerary.average_rating.toFixed(1)}
                          </span>
                          <span className="text-xs text-neutral-400">
                            ({itinerary.total_ratings} {itinerary.total_ratings === 1 ? 'rating' : 'ratings'})
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-neutral-400">
                          <FaRegStar className="text-sm" />
                          <span className="text-xs">No ratings yet</span>
                        </div>
                      )}
                      {!itinerary.user_has_rated && (
                        <span className="text-xs text-primary-500 font-medium">Rate it!</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Card Actions */}
                <div className="p-4 bg-gradient-to-r from-neutral-50 to-primary-50/30 border-t border-neutral-100 flex gap-2">
                  <button
                    onClick={() => onViewItinerary(itinerary.request_id)}
                    disabled={itinerary.status !== 'completed' || loadingItineraryId === itinerary.request_id}
                    className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                  >
                    {loadingItineraryId === itinerary.request_id ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <FaEye />
                        View
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => onRegenerateItinerary(itinerary)}
                    disabled={isTripEnded(itinerary.end_date)}
                    title={isTripEnded(itinerary.end_date) ? "Cannot regenerate - trip has ended" : "Regenerate with same parameters"}
                    className={`px-3 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all duration-300 ${
                      isTripEnded(itinerary.end_date)
                        ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-accent-500 to-accent-600 text-white hover:shadow-lg'
                    }`}
                  >
                    <FaRedo />
                  </button>
                  {/* Group Members - requires Group plan or higher */}
                  {!itinerary.is_shared && ['group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'].includes(user?.subscription_tier) && (
                    <button
                      onClick={() => handleMembersClick(itinerary.request_id)}
                      title="Manage group members"
                      className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-2.5 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                    >
                      <FaUsers />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(itinerary.request_id)}
                    title="Delete itinerary"
                    className="bg-gradient-to-r from-coral-500 to-red-500 text-white px-3 py-2.5 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Member Itineraries Section - Trips where user is a member */}
      {memberItineraries.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-golden-400 to-secondary-400 rounded-xl flex items-center justify-center shadow-lg">
              <FaUsers className="text-2xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-neutral-800 font-display">Trips You're Invited To</h2>
              <p className="text-neutral-500 text-sm">{memberItineraries.length} shared adventure{memberItineraries.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {memberItineraries.map((itinerary, index) => {
              const tripEnded = isTripEnded(itinerary.end_date);
              return (
              <div
                key={itinerary.request_id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-2 animate-fade-in-up ring-2 ring-golden-100"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {/* Card Header - Light Warm Sunset Theme */}
                <div className="relative bg-gradient-to-r from-golden-200 via-secondary-200 to-golden-300 p-5 overflow-hidden">
                  {/* Decorative circles */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/30 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                  {/* Shared + read-only badges */}
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 z-10">
                    <div className="bg-white/60 backdrop-blur-sm px-2 py-1 rounded-full">
                      <span className="text-secondary-700 text-xs font-semibold flex items-center gap-1">
                        <FaUsers className="text-xs" /> Shared
                      </span>
                    </div>
                    {tripEnded && (
                      <div
                        className="bg-neutral-700/80 backdrop-blur-sm px-2 py-1 rounded-full"
                        title="This trip has ended. You can still view it, but editing is no longer available."
                      >
                        <span className="text-white text-xs font-semibold flex items-center gap-1">
                          <FaEye className="text-xs" /> Read-only
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="relative flex justify-between items-start">
                    {getStatusBadge(itinerary.status)}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-5 space-y-4">
                  {/* Owner Info */}
                  <div className="bg-gradient-to-r from-golden-50 to-secondary-50 rounded-xl p-3 flex items-center gap-3 border border-golden-100">
                    <div className="w-8 h-8 bg-gradient-to-br from-golden-300 to-secondary-300 rounded-full flex items-center justify-center">
                      <FaUsers className="text-white text-sm" />
                    </div>
                    <span className="text-sm text-neutral-700">
                      Hosted by <strong className="text-secondary-600">{itinerary.owner_name}</strong>
                    </span>
                  </div>

                  {/* Route Display */}
                  <div className="relative">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 bg-gradient-to-br from-golden-300 to-golden-400 rounded-full flex items-center justify-center shadow-sm">
                          <FaMapMarkerAlt className="text-white text-xs" />
                        </div>
                        <div className="w-0.5 h-5 bg-gradient-to-b from-golden-300 to-secondary-300 my-1"></div>
                        <div className="w-7 h-7 bg-gradient-to-br from-secondary-300 to-secondary-400 rounded-full flex items-center justify-center shadow-sm">
                          <FaMapMarkerAlt className="text-white text-xs" />
                        </div>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div>
                          <p className="text-xs text-neutral-400 uppercase tracking-wider font-semibold">From</p>
                          <p className="font-bold text-neutral-800 text-sm">{itinerary.start_location}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-400 uppercase tracking-wider font-semibold">To</p>
                          <p className="font-bold text-neutral-800 text-sm">{itinerary.end_location}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trip Dates */}
                  {itinerary.start_date && (
                    <div className="flex items-center gap-2 text-neutral-600 text-sm bg-golden-50 p-2 rounded-lg">
                      <FaCalendarAlt className="text-secondary-400" />
                      <span className="font-medium">
                        {itinerary.start_date}
                        {itinerary.end_date && ` → ${itinerary.end_date}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card Actions */}
                <div className="p-4 bg-gradient-to-r from-golden-50 to-secondary-50 border-t border-golden-100 flex gap-2">
                  <button
                    onClick={() => onViewItinerary(itinerary.request_id, false)}
                    disabled={itinerary.status !== 'completed' || loadingItineraryId === itinerary.request_id}
                    className="flex-1 bg-gradient-to-r from-golden-400 to-secondary-400 text-white px-4 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:from-golden-500 hover:to-secondary-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingItineraryId === itinerary.request_id ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <FaEye />
                        View & Chat
                      </>
                    )}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={handleDeleteCancel}
        >
          <div
            className="bg-white rounded-2xl shadow-floating max-w-md w-full p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-neutral-800 mb-3">Delete Itinerary?</h3>
            <p className="text-neutral-600 mb-6">
              Are you sure you want to delete this itinerary? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteCancel}
                className="flex-1 px-4 py-3 rounded-lg font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-3 rounded-lg font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Members Modal */}
      {membersModalOpen && (
        <GroupMembers
          itineraryId={membersItineraryId}
          onClose={() => setMembersModalOpen(false)}
        />
      )}
      </div>
    </>
  );
};

export default Dashboard;
